import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/base/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/ui/base/command";
import { Checkbox } from "@/ui/base/checkbox";
import { Button } from "@/ui/base/button";
import { ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/nucleo/utilitarios";

export interface MultiSelectOption {
  /** Unique value for the option */
  value: string;
  /** Primary display label */
  label: string;
  /** Optional secondary label (e.g. code displayed in mono) */
  code?: string;
}

interface MultiSelectComboboxProps {
  /** Available options */
  options: MultiSelectOption[];
  /** Currently selected values */
  selected: string[];
  /** Called when selection changes */
  onChange: (selected: string[]) => void;
  /** Placeholder when nothing is selected */
  placeholder?: string;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Empty search results message */
  emptyMessage?: string;
  /** Label for the clear button */
  clearLabel?: string;
  /** Function to render the trigger text when items are selected */
  renderSelectedText?: (count: number) => string;
  /** Width class for the trigger */
  triggerClassName?: string;
  /** Width class for the popover content */
  popoverClassName?: string;
  /** Alignment of the popover */
  align?: "start" | "center" | "end";
  /** Whether the combobox is disabled */
  disabled?: boolean;
  /**
   * Accessible name for the trigger button. `role="combobox"` does NOT take
   * its name from visible content per the ARIA naming spec (unlike
   * `button`/`link`) — without this, the trigger has an EMPTY accessible
   * name for anyone using a screen reader, even though sighted users see the
   * placeholder/selected-text. Achado real: nenhum consumidor deste
   * componente existia até `operacional/inscricoes/InscricoesListaPage.tsx`
   * (filtro de DRE) expor isso.
   */
  ariaLabel?: string;
}

export function MultiSelectCombobox({
  options,
  selected,
  onChange,
  placeholder = "Selecionar...",
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhum item encontrado.",
  clearLabel = "Limpar seleção",
  renderSelectedText,
  triggerClassName,
  popoverClassName,
  align = "start",
  disabled = false,
  ariaLabel,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    const current = new Set(selected);
    if (current.has(value)) {
      current.delete(value);
    } else {
      current.add(value);
    }
    onChange(Array.from(current));
  };

  const clear = () => onChange([]);

  const selectedText = renderSelectedText
    ? renderSelectedText(selected.length)
    : `${selected.length} selecionado${selected.length > 1 ? "s" : ""}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "flex h-11 w-full items-center justify-between rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm ring-offset-background transition-colors",
            "focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName
          )}
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground/80">{placeholder}</span>
          ) : (
            <span className="truncate">{selectedText}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-[280px] p-0", popoverClassName)} align={align}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.code ? `${opt.code} ${opt.label}` : opt.label}
                  onSelect={() => toggle(opt.value)}
                >
                  <Checkbox
                    checked={selected.includes(opt.value)}
                    className="mr-2"
                  />
                  {opt.code && (
                    <span className="font-mono text-xs mr-2">{opt.code}</span>
                  )}
                  <span className="text-sm truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected.length > 0 && (
          <div className="border-t p-2">
            <Button variant="ghost" size="sm" className="w-full" onClick={clear}>
              <X className="h-3.5 w-3.5 mr-1" /> {clearLabel}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
